import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdDownload, MdSearch, MdShowChart, MdCandlestickChart } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import { useAuth } from '../../context/AuthContext';
import PlotlyChart from '../../components/charts/PlotlyChart';

const formatMarketCap = (cap) => {
  if (!cap) return '--';
  if (cap >= 1e12) return `$${(cap / 1e12).toFixed(1)}T`;
  if (cap >= 1e9) return `$${(cap / 1e9).toFixed(1)}B`;
  if (cap >= 1e6) return `$${(cap / 1e6).toFixed(0)}M`;
  return `$${cap.toLocaleString()}`;
};

const SymbolsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSymbols, setNewSymbols] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [prices, setPrices] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [backfilling, setBackfilling] = useState({});
  const [chartType, setChartType] = useState('line');
  const [addOpen, setAddOpen] = useState(false);

  const loadSymbols = useCallback((search = '') => {
    setLoading(true);
    tradingService.getSymbols(search)
      .then(res => setSymbols(res.data.results || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSymbols(); }, [loadSymbols]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadSymbols(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, loadSymbols]);

  const handleAdd = async () => {
    const syms = newSymbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    if (!syms.length) return;
    setAdding(true);
    try {
      await tradingService.addSymbols(syms);
      setNewSymbols('');
      setTimeout(loadSymbols, 4000);
    } catch (_) {
      // silent
    } finally {
      setAdding(false);
    }
  };

  const handleBackfill = async (sym, source = 'historical') => {
    setBackfilling(prev => ({ ...prev, [sym.id]: true }));
    try {
      await tradingService.triggerSync([sym.symbol], source);
    } catch (_) {
      // silent
    } finally {
      setBackfilling(prev => ({ ...prev, [sym.id]: false }));
    }
  };

  const loadPrices = async (sym) => {
    setSelectedSymbol(sym);
    setPriceLoading(true);
    try {
      const res = await tradingService.getSymbolPrices(sym.id, 365);
      setPrices(res.data || []);
    } catch (_) {
      setPrices([]);
    } finally {
      setPriceLoading(false);
    }
  };

  const filteredSymbols = symbols;

  const buildChartData = () => {
    if (!prices || prices.length === 0) return null;

    if (chartType === 'candlestick') {
      return [{
        x: prices.map(p => p.date),
        open: prices.map(p => parseFloat(p.open)),
        high: prices.map(p => parseFloat(p.high)),
        low: prices.map(p => parseFloat(p.low)),
        close: prices.map(p => parseFloat(p.close)),
        type: 'candlestick',
        name: selectedSymbol?.symbol,
        increasing: { line: { color: '#22c55e' } },
        decreasing: { line: { color: '#ef4444' } },
      }];
    }

    return [{
      x: prices.map(p => p.date),
      y: prices.map(p => parseFloat(p.close)),
      type: 'scatter',
      mode: 'lines',
      name: selectedSymbol?.symbol,
      line: { color: '#c9a96e', width: 2 },
      fill: 'tozeroy',
      fillcolor: 'rgba(201, 169, 110, 0.08)',
    }];
  };

  const SourceBadge = ({ sym, source, label }) => {
    const count = sym.data_sources?.[source] || 0;
    const verified = sym.metadata?.[`${source}_status`] === 'verified';
    if (count === 0) {
      return (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-400 line-through">
          {label}
        </span>
      );
    }
    return (
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
        verified
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
      }`}>
        {label} {verified ? '✓' : count.toLocaleString()}
      </span>
    );
  };

  const priceChart = buildChartData();
  const cardCls = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4';
  const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-serif font-semibold text-gray-800 dark:text-gray-200">SYMBOLS & MARKET DATA</h2>
        {isAdmin && (
          <button
            onClick={() => setAddOpen(!addOpen)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-brand-gold text-white hover:bg-brand-gold-dark font-medium"
          >
            <MdAdd size={14} /> Add Symbols
          </button>
        )}
      </div>

      {/* Add symbols - collapsible */}
      {addOpen && (
        <div className={cardCls}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">ADD SYMBOLS</h3>
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              placeholder="AAPL, MSFT, SPY, QQQ..."
              value={newSymbols}
              onChange={(e) => setNewSymbols(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className={`flex-1 min-w-[200px] ${inputCls}`}
            />
            <button
              onClick={handleAdd}
              disabled={adding || !newSymbols.trim()}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40"
            >
              <MdAdd size={14} /> {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 uppercase tracking-wide">Comma-separated tickers. Profiles fetched from Yahoo Finance.</p>
        </div>
      )}

      {/* Symbol list + price chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Symbol list */}
        <div className={`${cardCls} lg:col-span-1`}>
          <div className="flex items-center gap-2 mb-3">
            <MdSearch className="text-gray-400 shrink-0" size={16} />
            <input
              type="text"
              placeholder="Search symbols, sectors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 outline-none border-b border-gray-200 dark:border-gray-700 pb-1 placeholder-gray-400"
            />
          </div>

          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">
            {filteredSymbols.length} symbol{filteredSymbols.length !== 1 ? 's' : ''}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brand-gold" />
            </div>
          ) : filteredSymbols.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {symbols.length === 0 ? 'No symbols tracked yet. Add some above.' : 'No results for your search.'}
            </p>
          ) : (
            <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 scrollbar-thin">
              {filteredSymbols.map(sym => (
                <div
                  key={sym.id}
                  onClick={() => loadPrices(sym)}
                  className={`py-2.5 px-2 cursor-pointer rounded transition-colors ${
                    selectedSymbol?.id === sym.id
                      ? 'bg-brand-gold/10 border-l-2 border-brand-gold'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{sym.symbol}</span>
                        {sym.is_etf && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">ETF</span>}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">{sym.name}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {sym.exchange && <span className="text-[10px] text-gray-400">{sym.exchange}</span>}
                        {sym.sector && <span className="text-[10px] text-gray-400">| {sym.sector}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                      <span className="text-xs text-gray-500 tabular-nums">{formatMarketCap(sym.market_cap)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleBackfill(sym); }}
                        disabled={backfilling[sym.id]}
                        className="p-1 text-gray-400 hover:text-brand-gold transition-colors"
                        title="Backfill historical data"
                      >
                        <MdDownload size={14} className={backfilling[sym.id] ? 'animate-pulse' : ''} />
                      </button>
                    </div>
                  </div>
                  {/* Source badges */}
                  <div className="flex gap-1 mt-1.5">
                    <SourceBadge sym={sym} source="yfinance" label="yfinance" />
                    <SourceBadge sym={sym} source="fmp" label="fmp" />
                    <SourceBadge sym={sym} source="ibkr" label="ibkr" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price chart */}
        <div className={`${cardCls} lg:col-span-2`}>
          {selectedSymbol ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-serif font-semibold text-gray-800 dark:text-gray-200">
                    {selectedSymbol.symbol}
                  </h3>
                  <p className="text-xs text-gray-500">{selectedSymbol.name}</p>
                </div>
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                  <button
                    onClick={() => setChartType('line')}
                    className={`p-1.5 rounded text-xs ${chartType === 'line' ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm' : 'text-gray-500'}`}
                    title="Line chart"
                  >
                    <MdShowChart size={16} />
                  </button>
                  <button
                    onClick={() => setChartType('candlestick')}
                    className={`p-1.5 rounded text-xs ${chartType === 'candlestick' ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm' : 'text-gray-500'}`}
                    title="Candlestick"
                  >
                    <MdCandlestickChart size={16} />
                  </button>
                </div>
              </div>
              {priceLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brand-gold" />
                </div>
              ) : priceChart ? (
                <PlotlyChart
                  data={priceChart}
                  layout={{
                    yaxis: {
                      title: 'Price ($)',
                      tickformat: '$.2f',
                      gridcolor: undefined,
                    },
                    xaxis: {
                      rangeslider: chartType === 'candlestick' ? { visible: false } : undefined,
                    },
                    showlegend: false,
                  }}
                />
              ) : (
                <div className="text-center py-16 text-gray-500">
                  <MdDownload size={28} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No price data available.</p>
                  <p className="text-xs text-gray-400 mt-1">Click the download icon to backfill historical data.</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20 text-gray-500">
              <MdShowChart size={36} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm">Select a symbol to view price chart</p>
              <p className="text-xs text-gray-400 mt-1">Click any symbol from the list</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SymbolsPage;
